import React, { useState } from "react";
import moment from "moment-timezone";
import humanize from "underscore.string/humanize";
import { TaskConfigParamOptions, TaskFunctionParams } from "@eave/shared/types/api";
import {
  DatePicker,
  DatePickerInput,
  Select,
  SelectItem,
  TextInput,
} from "carbon-components-react";
import Validations from "web_modules/shared/FormHelpers/validation";
import getHandlers from "web_modules/shared/FormHelpers/eventHandlers";
import { TaskConfigFilterType, TaskConfigSchemaDef } from "./shared";
import EditTaskClauseDateParameters from "./TaskClauseDateParameters";

interface TaskClauseProps {
  disabled?: boolean;
  taskOptions: TaskConfigParamOptions;
  params: TaskFunctionParams;
  setParams: Function;
  taskConfigDefinition: TaskConfigSchemaDef;
}

const TaskFuncParams: React.FC<TaskClauseProps> = ({
  disabled,
  taskOptions,
  params,
  setParams,
  taskConfigDefinition,
}) => {
  const [invalidFields, setInvalidFields] = useState([]);
  const {
    handleTextInputChange,
    handleCurrencyInputChange,
    handleFloatInputChange,
    handleSelectChange,
  } = getHandlers(params, setParams, invalidFields, setInvalidFields);

  const id = `id-${Math.random()}`;
  return (
    <div style={{ marginRight: "30px" }}>
      {taskConfigDefinition && (
        <>
          {taskConfigDefinition.type === TaskConfigFilterType.TEXT && (
            <TextInput
              disabled={disabled}
              id={`task-config-${taskConfigDefinition.func_name}-string`}
              name="string"
              value={(params && params.string) || ""}
              labelText={`Text value for ${taskConfigDefinition.displayText}*`}
              placeholder="Enter a text value"
              type="text"
              invalid={invalidFields.includes("string")}
              invalidText="Enter a valid text value"
              onChange={e =>
                handleTextInputChange(e, taskConfigDefinition.comparisonKey, [Validations.IsValid])
              }
              size="sm"
            />
          )}
          {(taskConfigDefinition.type === TaskConfigFilterType.CURRENCY ||
            taskConfigDefinition.type === TaskConfigFilterType.AMOUNT) && (
            <>
              <Select
                disabled={disabled}
                id="task-function-name"
                name="decimal_operator"
                value={(params && params.decimal_operator) || ""}
                invalid={invalidFields.includes("decimal_operator")}
                invalidText="Select a valid decimal operator"
                labelText="Condition: Decimal Operator*"
                onChange={e => handleSelectChange(e, "decimal_operator")}
                size="sm"
              >
                <SelectItem text="Select an operator" value="" />
                {taskOptions.decimal_operators.map(op => (
                  <SelectItem key={op} text={op} value={op} />
                ))}
              </Select>
              <div style={{ marginBottom: "10px" }} />
            </>
          )}
          {taskConfigDefinition.type === TaskConfigFilterType.DATE_COMPARISON && (
            <EditTaskClauseDateParameters
              options={taskOptions}
              fieldName={`date_comparison_params-${id}`}
              formState={params}
              setFormState={e => {
                setParams({ ...params, ...e });
              }}
            />
          )}
          {taskConfigDefinition.type === TaskConfigFilterType.CURRENCY && (
            <TextInput
              disabled={disabled}
              id={`task-config-${taskConfigDefinition.func_name}-amount`}
              name="amount"
              value={(params && params.amount) || ""}
              labelText={`Text value for ${taskConfigDefinition.displayText}*`}
              placeholder="Enter a currency amount"
              type="text"
              invalid={invalidFields.includes("amount")}
              invalidText="Enter a valid amount"
              onBlur={e => {
                handleCurrencyInputChange(
                  e,
                  taskConfigDefinition.comparisonKey,
                  [Validations.IsValid, Validations.IsNumber],
                  true, // convert to float before processing
                );
              }}
              onChange={e =>
                handleTextInputChange(e, taskConfigDefinition.comparisonKey, [Validations.IsValid])
              }
              size="sm"
            />
          )}
          {taskConfigDefinition.type === TaskConfigFilterType.AMOUNT && (
            <TextInput
              disabled={disabled}
              id={`task-config-${taskConfigDefinition.func_name}-amount`}
              name={taskConfigDefinition.comparisonKey}
              value={(params && params.amount) || ""}
              labelText={`Text value for ${taskConfigDefinition.displayText}*`}
              placeholder="Enter an amount"
              type="text"
              invalid={invalidFields.includes("amount")}
              invalidText="Enter a valid amount"
              onChange={e =>
                handleFloatInputChange(e, taskConfigDefinition.comparisonKey, [Validations.IsValid])
              }
              size="sm"
            />
          )}
          {taskConfigDefinition.type === TaskConfigFilterType.ENUM && (
            <Select
              disabled={disabled}
              id="task-function-name"
              name={taskConfigDefinition.comparisonKey}
              value={(params && params[taskConfigDefinition.comparisonKey]) || ""}
              invalid={invalidFields.includes(taskConfigDefinition.comparisonKey)}
              invalidText="Select a valid option"
              labelText="Condition: Select option*"
              onChange={e => handleSelectChange(e, taskConfigDefinition.comparisonKey)}
              size="sm"
            >
              <SelectItem text="Select an option" value="" />
              {taskConfigDefinition.options.map(option => (
                <SelectItem key={option} text={humanize(option)} value={option} />
              ))}
            </Select>
          )}
          {taskConfigDefinition.type === TaskConfigFilterType.DATE && (
            <DatePicker
              id="date-picker-start-date-single"
              datePickerType="single"
              dateFormat="Y-m-d"
              style={{ marginBottom: "10px" }}
              value={params && params[taskConfigDefinition.comparisonKey]}
              onChange={e =>
                handleTextInputChange(
                  {
                    target: {
                      value: moment(e[0]).format("YYYY-MM-DD"),
                    },
                  },
                  taskConfigDefinition.comparisonKey,
                )
              }
            >
              <DatePickerInput
                id="date-picker-start-date-single-input"
                name="start_date_single"
                labelText="Enter a valid date *"
                placeholder="yyyy-mm-dd"
                type="text"
                size="sm"
                disabled={disabled}
              />
            </DatePicker>
          )}
        </>
      )}
    </div>
  );
};
export default TaskFuncParams;
